import * as ents from '@attain-apps/entities'
import { TypeChecker } from '@attain-apps/type-guards'

export interface Section {
  readonly id: Section.Id
  readonly icon: Section.Icon
  readonly name: Section.Name
}
export namespace Section {
  export type Id = ents.core.admin.Scenario.Visibility

  export enum Icon {
    Pinned = 'atn-pin',
    Visible = 'atn-cluster',
    Hidden = 'atn-hide',
  }
  const ICONS: ReadonlySet<Icon> = new Set(Object.values(Icon) as Icon[])
  export namespace Icon {
    export const ALL: readonly Icon[] = Array.from(ICONS)
    export const isIcon = TypeChecker.Factory.oneOf(ICONS)

    export function fromId(sectionId: Section.Id): Icon {
      switch (sectionId) {
        case ents.core.admin.Scenario.Visibility.Pinned:
          return Icon.Pinned
        case ents.core.admin.Scenario.Visibility.Visible:
          return Icon.Visible
        case ents.core.admin.Scenario.Visibility.Hidden:
          return Icon.Hidden
        default:
          return Icon.Hidden
      }
    }
  }

  export enum Name {
    Pinned = 'Pinned',
    Visible = 'Visible',
    Hidden = 'Hidden',
  }
  const NAMES: ReadonlySet<Name> = new Set(Object.values(Name) as Name[])
  export namespace Name {
    export const ALL: readonly Name[] = Array.from(NAMES)
    export const isName = TypeChecker.Factory.oneOf(NAMES)

    export function fromId(sectionId: Section.Id): Name {
      switch (sectionId) {
        case ents.core.admin.Scenario.Visibility.Pinned:
          return Name.Pinned
        case ents.core.admin.Scenario.Visibility.Visible:
          return Name.Visible
        case ents.core.admin.Scenario.Visibility.Hidden:
          return Name.Hidden
        default:
          return Name.Hidden
      }
    }
  }

  export interface WithScenarios extends Section {
    readonly scenarios: ents.core.admin.Scenario[]
  }

  export function from(
    scenarios: readonly ents.core.admin.Scenario[],
  ): readonly Section.WithScenarios[] {
    return scenarios
      .reduce<Section.WithScenarios[]>((sections, scenario) => {
        const section = sections.find(
          _section => _section.id === scenario.visibility,
        )

        if (section) {
          section.scenarios.push(scenario)
        } else {
          sections.push({
            icon: Section.Icon.fromId(scenario.visibility),
            id: scenario.visibility,
            name: Section.Name.fromId(scenario.visibility),
            scenarios: [scenario],
          })
        }

        return sections
      }, [])
      .sort((a, b) => a.id - b.id)
  }

  /**
   * Returns the visibility sections other than the given section.
   */
  export function otherSectionsFrom(section: Section.Id): readonly Section[] {
    return ents.core.admin.Scenario.Visibility.ALL.filter(
      id => id !== section,
    ).map(id => ({
      icon: Section.Icon.fromId(id),
      id,
      name: Section.Name.fromId(id),
    }))
  }
}
